// src/services/dashboardService.js

import { apiService } from './api'

class DashboardService {
  async getSummary(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch summary')
    }
  }

  async getChartsData(filters = {}) {
    try {
      const response = await apiService.get('/analytics/stats', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch charts data')
    }
  }

  async getRecentTransactions(filters = {}) {
    try {
      const response = await apiService.get('/parking/events', {
        ...filters,
        limit: 10,
        sort: 'createdAt',
        order: 'desc'
      })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recent transactions')
    }
  }

  async getDashboardStats(filters = {}) {
    try {
      const [summary, charts, transactions] = await Promise.all([
        this.getSummary(filters),
        this.getChartsData(filters),
        this.getRecentTransactions(filters)
      ])

      return {
        summary,
        charts,
        recentTransactions: transactions
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard stats')
    }
  }

  async getAdminDashboardStats(filters = {}) {
    try {
      const response = await apiService.get('/analytics/dashboard-stats', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch admin dashboard stats')
    }
  }

  async getRevenueStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch revenue stats')
    }
  }

  async getTransactionStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch transaction stats')
    }
  }

  async getVehicleStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch vehicle stats')
    }
  }

  async getPaymentStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payment stats')
    }
  }

  async getLocationStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location stats')
    }
  }

  async getUserStats(filters = {}) {
    try {
      const response = await apiService.get('/reports/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user stats')
    }
  }

  async getDeviceStats(filters = {}) {
    try {
      const response = await apiService.get('/analytics/stats', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch device stats')
    }
  }

  async getTopupStats(filters = {}) {
    try {
      const response = await apiService.get('/payments/summary', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch topup stats')
    }
  }

  async getRealTimeData() {
    try {
      const response = await apiService.get('/analytics/summary')
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch real-time data')
    }
  }

  async getNotifications() {
    try {
      const response = await apiService.get('/notifications')
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch notifications')
    }
  }

  async markNotificationAsRead(notificationId) {
    try {
      const response = await apiService.put(`/notifications/${notificationId}`, {
        status: 'read'
      })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark notification as read')
    }
  }

  async getSystemHealth() {
    try {
      const response = await apiService.get('/stock/health') // Example health check
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch system health')
    }
  }

  async getPerformanceMetrics(filters = {}) {
    try {
      const response = await apiService.get('/analytics/stats', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch performance metrics')
    }
  }

  async exportDashboardData(format = 'excel', filters = {}) {
    try {
      const response = await apiService.get(`/reports/${format}`, filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export dashboard data')
    }
  }


  async getCustomReports(filters = {}) {
    try {
      const response = await apiService.get('/reports/reports/custom-reports', filters)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch custom reports')
    }
  }

  async createCustomReport(reportData) {
    try {
      const response = await apiService.post('/reports/reports/custom-reports', reportData)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create custom report')
    }
  }

  async updateCustomReport(reportId, reportData) {
    try {
      const response = await apiService.put(`/reports/reports/custom-reports/${reportId}`, reportData)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update custom report')
    }
  }

  async deleteCustomReport(reportId) {
    try {
      const response = await apiService.delete(`/reports/reports/custom-reports/${reportId}`)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete custom report')
    }
  }
}

export default new DashboardService()
